/**
 * Hook that bridges the data layer filter mechanism with operator proficiency filters.
 * Uses metadata from backend to populate filter options while keeping existing component designs.
 */

import { useMemo, useCallback } from 'react';
import { useOperatorProficiency } from '@/contexts/OperatorProficiencyContext';

export interface FilterOption {
  id: string;
  label: string;
  value: string | number;
  checked: boolean;
}

export interface FilterConfig {
  id: string;
  type: 'dropdown' | 'multi-select' | 'checkbox';
  label: string;
  options: FilterOption[];
  isMultiSelect: boolean;
  [key: string]: unknown;
}

/**
 * Hook for managing operator proficiency filters using data layer mechanism
 */
export const useOperatorProficiencyFilters = () => {
  const { metadata, filters, updateFilter, loading } = useOperatorProficiency();

  /**
   * Get filter configuration by ID with current selection state
   */
  const getFilterConfig = useCallback(
    (filterId: string): FilterConfig | null => {
      if (!metadata) return null;

      const filterMeta = metadata.filters?.find((f) => f.id === filterId);
      if (!filterMeta) return null;

      const currentValue = filters[filterId as keyof typeof filters];
      const isMultiSelect = filterMeta.type === 'multi-select';

      return {
        id: filterMeta.id,
        type: (filterMeta.type || 'dropdown') as FilterConfig['type'],
        label: filterMeta.label || 'Unknown Filter',
        isMultiSelect,
        options: (filterMeta.options || []).map((opt) => ({
          id: String(opt.id),
          label: opt.label,
          value: opt.value,
          checked: currentValue !== undefined
            ? (isMultiSelect
                ? Array.isArray(currentValue) && currentValue.includes(String(opt.value))
                : String(currentValue) === String(opt.value))
            : opt.checked ?? false, // Use original metadata checked value if currentValue is undefined
        })),
      };
    },
    [metadata, filters]
  );

  /**
   * Asset type filter configuration
   */
  const assetTypeFilter = useMemo(() => getFilterConfig('asset_type'), [getFilterConfig]);

  /**
   * Months range filter configuration
   */
  const monthsRangeFilter = useMemo(() => getFilterConfig('months_range'), [getFilterConfig]);

  /**
   * Handle single-select filter change (like months_range)
   */
  const handleSingleSelect = useCallback(
    (filterId: string, value: string | number) => {
      const numValue = typeof value === 'string' ? parseInt(value, 10) : value;

      // Validate that we have a valid positive number
      if (typeof numValue !== 'number' || isNaN(numValue) || numValue <= 0) {
        return;
      }

      const currentValue = filters[filterId as keyof typeof filters];

      // Skip update if value hasn't changed
      if (currentValue === numValue) {
        return;
      }

      updateFilter(filterId, numValue);
    },
    [updateFilter, filters]
  );

  /**
   * Handle multi-select filter change (like asset_type)
   * Prevents removing the last item - at least one must be selected
   */
  const handleMultiSelect = useCallback(
    (filterId: string, optionId: string, checked: boolean) => {
      const currentValue = filters[filterId as keyof typeof filters];
      const currentArray = Array.isArray(currentValue) ? currentValue : [];

      // If unchecking and this is the last item, don't allow it
      if (!checked && currentArray.length <= 1) {
        return; // At least one option must be selected
      }

      const newValue = checked
        ? [...currentArray, optionId]
        : currentArray.filter((v) => v !== optionId);

      updateFilter(filterId, newValue);
    },
    [filters, updateFilter]
  );

  /**
   * Handle setting all values for multi-select (select all / clear all)
   */
  const handleSetMultiSelectValues = useCallback(
    (filterId: string, values: string[]) => {
      updateFilter(filterId, values);
    },
    [updateFilter]
  );

  /**
   * Get current filter value
   */
  const getFilterValue = useCallback(
    <T = any>(filterId: string): T => {
      return filters[filterId as keyof typeof filters] as T;
    },
    [filters]
  );

  return {
    // Filter configurations
    assetTypeFilter,
    monthsRangeFilter,

    // Current values
    filters,
    getFilterValue,

    // Handlers
    handleSingleSelect,
    handleMultiSelect,
    handleSetMultiSelectValues,
    updateFilter,

    // Generic getter
    getFilterConfig,

    // Loading state
    loading,
  };
};
